import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as MUIIcons from '@mui/icons-material';

// Popular SCADA icons
const scadaIcons = [
  'Dashboard', 'Settings', 'PowerSettingsNew', 'PlayArrow', 'Stop', 'Pause',
  'Speed', 'Thermostat', 'WaterDrop', 'ElectricBolt', 'Insights',
  'TrendingUp', 'ShowChart', 'BarChart', 'PieChart', 'Timeline',
  'Warning', 'Error', 'CheckCircle', 'Info', 'Notifications',
  'Build', 'Construction', 'Engineering', 'Handyman', 'HomeRepairService',
  'Factory', 'Precision Manufacturing', 'Memory', 'Storage', 'CloudQueue',
  'Widgets', 'ViewModule', 'GridView', 'Apps', 'Extension',
  'ToggleOn', 'ToggleOff', 'RadioButtonOn', 'RadioButtonOff', 'CheckBox',
  'CropSquare', 'Circle', 'Remove', 'Add', 'Close',
  'ArrowForward', 'ArrowBack', 'ArrowUpward', 'ArrowDownward', 'Refresh',
  'Visibility', 'VisibilityOff', 'Lock', 'LockOpen', 'Security'
];

// Widget type specific icons
const widgetTypeIcons = {
  button: ['TouchApp', 'SmartButton', 'PowerSettingsNew', 'PlayArrow', 'Stop', 'Pause'],
  'led-indicator': ['Circle', 'FiberManualRecord', 'RadioButtonOn', 'Brightness1'],
  gauge: ['Speed', 'Thermostat', 'SpeedIcon', 'DonutSmall'],
  'trend-chart': ['ShowChart', 'TrendingUp', 'Timeline', 'Insights'],
  'text-label': ['TextFields', 'Label', 'Title', 'ShortText'],
  'toggle-switch': ['ToggleOn', 'ToggleOff', 'Switch'],
  image: ['Image', 'Photo', 'Wallpaper', 'Collections'],
  'progress-bar': ['LinearProgress', 'HorizontalRule', 'Timeline'],
  'combo-box': ['ArrowDropDown', 'List', 'ViewList', 'Menu'],
  box: ['CropSquare', 'Crop169', 'Rectangle', 'ViewModule'],
  line: ['Remove', 'HorizontalRule', 'Timeline', 'LinearScale'],
  circle: ['Circle', 'RadioButtonUnchecked', 'Brightness1', 'FiberManualRecord']
};

const IconSelector = ({ open, onClose, onSelect, currentIcon, widgetType = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('scada');

  // Get all available icons
  const allIconNames = Object.keys(MUIIcons).filter(name => 
    name !== 'default' && 
    typeof MUIIcons[name] === 'function' &&
    !name.includes('Outlined') &&
    !name.includes('TwoTone') &&
    !name.includes('Sharp')
  );

  // Filter icons based on category and search
  const getFilteredIcons = () => {
    let iconList = [];
    
    if (selectedCategory === 'scada') {
      iconList = scadaIcons;
    } else if (selectedCategory === 'widget-type' && widgetType && widgetTypeIcons[widgetType]) {
      iconList = widgetTypeIcons[widgetType];
    } else if (selectedCategory === 'all') {
      iconList = allIconNames.slice(0, 200); // Limit to first 200 for performance
    }

    if (searchTerm) {
      iconList = iconList.filter(name => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return iconList;
  };

  const handleIconSelect = (iconName) => {
    onSelect(iconName);
    onClose();
  };

  const IconComponent = ({ name }) => {
    const Icon = MUIIcons[name];
    return Icon ? <Icon /> : null;
  };

  const categories = [
    { key: 'scada', label: 'SCADA Icons', count: scadaIcons.length },
    ...(widgetType && widgetTypeIcons[widgetType] ? 
      [{ key: 'widget-type', label: `${widgetType} Icons`, count: widgetTypeIcons[widgetType].length }] : 
      []
    ),
    { key: 'all', label: 'All Icons', count: '200+' }
  ];

  const filteredIcons = getFilteredIcons();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: 600 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Select Icon</Typography>
          {currentIcon && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">Current:</Typography>
              <IconComponent name={currentIcon} />
              <Typography variant="body2">{currentIcon}</Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Categories */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>Categories:</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {categories.map(category => (
              <Chip
                key={category.key}
                label={`${category.label} (${category.count})`}
                onClick={() => setSelectedCategory(category.key)}
                color={selectedCategory === category.key ? 'primary' : 'default'}
                variant={selectedCategory === category.key ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
        </Box>

        {/* Icon Grid */}
        <Box sx={{ height: 400, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
          <Grid container spacing={1}>
            {filteredIcons.map((iconName) => (
              <Grid item xs={2} sm={1.5} md={1.2} key={iconName}>
                <IconButton
                  onClick={() => handleIconSelect(iconName)}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    border: '1px solid transparent',
                    borderRadius: 1,
                    flexDirection: 'column',
                    gap: 0.5,
                    fontSize: '0.7rem',
                    '&:hover': {
                      border: '1px solid primary.main',
                      backgroundColor: 'primary.50'
                    },
                    ...(currentIcon === iconName && {
                      backgroundColor: 'primary.100',
                      border: '2px solid primary.main'
                    })
                  }}
                  title={iconName}
                >
                  <IconComponent name={iconName} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.6rem', 
                      textAlign: 'center',
                      wordBreak: 'break-all',
                      lineHeight: 1,
                      maxHeight: '2.4em',
                      overflow: 'hidden'
                    }}
                  >
                    {iconName}
                  </Typography>
                </IconButton>
              </Grid>
            ))}
          </Grid>
          
          {filteredIcons.length === 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <Typography color="text.secondary">No icons found</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={() => handleIconSelect('')} color="secondary">
          Clear Icon
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconSelector;